import type { BillingCatalog, BillingSubscriptionProduct } from '../types/billing';
import { isStripePriceId } from '../config/paymentLinkSeo';
import { isBillingSubscriptionPriceKey, isConsistentSubscriptionProduct } from './billingSubscriptionPlans';

export type LandingBillingCatalog = Pick<BillingCatalog, 'subscriptions' | 'freePlan'>;

declare global {
  interface Window {
    __PALEN_LANDING_BILLING_CATALOG__?: unknown;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return isFiniteNumber(value) && Number.isInteger(value) && value >= 0;
}

function isPositiveInteger(value: unknown): value is number {
  return isNonNegativeInteger(value) && value > 0;
}

function isNullableFiniteNumber(value: unknown): value is number | null {
  return value === null || isFiniteNumber(value);
}

function optionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function parseSubscription(value: unknown): BillingSubscriptionProduct | null {
  if (!isRecord(value)) return null;

  const priceKey = value.priceKey;
  const interval = value.interval;
  if (
    !isBillingSubscriptionPriceKey(priceKey) ||
    (interval !== 'monthly' && interval !== 'annual') ||
    !isConsistentSubscriptionProduct({ priceKey, interval, plan: value.plan }) ||
    (value.priceId !== undefined && !isStripePriceId(value.priceId)) ||
    typeof value.name !== 'string' ||
    value.name.trim() === '' ||
    !isPositiveInteger(value.amount) ||
    typeof value.currency !== 'string' ||
    !/^[A-Za-z]{3}$/.test(value.currency) ||
    typeof value.priceLabel !== 'string' ||
    value.priceLabel.trim() === '' ||
    !optionalString(value.monthlyEquivalentLabel) ||
    !optionalString(value.annualSavingsLabel) ||
    !isNonNegativeInteger(value.trialDays) ||
    !isNullableFiniteNumber(value.quotaCredits) ||
    (value.consultantQuotaCredits !== undefined && !isNullableFiniteNumber(value.consultantQuotaCredits)) ||
    (value.auditorQuotaCredits !== undefined && !isNullableFiniteNumber(value.auditorQuotaCredits)) ||
    !isFiniteNumber(value.quotaRenewalPeriodDays) ||
    !isNullableFiniteNumber(value.dailyAnalysesMin) ||
    !isNullableFiniteNumber(value.dailyAnalysesMax) ||
    !isNullableFiniteNumber(value.monthlyAnalysesMin) ||
    !isNullableFiniteNumber(value.monthlyAnalysesMax)
  ) {
    return null;
  }

  return {
    priceKey,
    ...(value.plan === 'pro' || value.plan === 'enterprise' ? { plan: value.plan } : {}),
    ...(value.priceId ? { priceId: value.priceId as string } : {}),
    name: value.name,
    interval,
    amount: value.amount,
    currency: value.currency,
    priceLabel: value.priceLabel,
    ...(value.monthlyEquivalentLabel ? { monthlyEquivalentLabel: value.monthlyEquivalentLabel } : {}),
    ...(value.annualSavingsLabel ? { annualSavingsLabel: value.annualSavingsLabel } : {}),
    trialDays: value.trialDays,
    quotaCredits: value.quotaCredits,
    ...(value.consultantQuotaCredits !== undefined ? { consultantQuotaCredits: value.consultantQuotaCredits } : {}),
    ...(value.auditorQuotaCredits !== undefined ? { auditorQuotaCredits: value.auditorQuotaCredits } : {}),
    quotaRenewalPeriodDays: value.quotaRenewalPeriodDays,
    dailyAnalysesMin: value.dailyAnalysesMin,
    dailyAnalysesMax: value.dailyAnalysesMax,
    monthlyAnalysesMin: value.monthlyAnalysesMin,
    monthlyAnalysesMax: value.monthlyAnalysesMax,
  };
}

export function parseLandingBillingCatalog(value: unknown): LandingBillingCatalog | null {
  if (!isRecord(value) || !Array.isArray(value.subscriptions)) return null;

  const subscriptions = value.subscriptions.map(parseSubscription);
  if (subscriptions.length === 0 || subscriptions.some((product) => product === null)) {
    return null;
  }

  const parsedSubscriptions = subscriptions as BillingSubscriptionProduct[];
  if (new Set(parsedSubscriptions.map((product) => product.priceKey)).size !== parsedSubscriptions.length) {
    return null;
  }

  const free = value.freePlan;
  if (free !== undefined && (!isRecord(free) || free.name !== 'Free' || free.quotaPool !== 'FREE'
    || !isPositiveInteger(free.quotaCredits) || !isPositiveInteger(free.quotaRenewalPeriodDays)
    || !isNonNegativeInteger(free.monthlyAnalysesMin) || !isNonNegativeInteger(free.monthlyAnalysesMax)
    || free.monthlyAnalysesMin > free.monthlyAnalysesMax)) return null;
  return { subscriptions: parsedSubscriptions, ...(free ? { freePlan: free as unknown as NonNullable<BillingCatalog['freePlan']> } : {}) };
}

export function requireLandingBillingCatalog(value: unknown): LandingBillingCatalog {
  const catalog = parseLandingBillingCatalog(value);
  if (!catalog) {
    throw new TypeError('The public billing catalog does not contain a valid subscription offer.');
  }
  return catalog;
}

export function readPrerenderedLandingBillingCatalog(): LandingBillingCatalog | undefined {
  if (typeof window === 'undefined') return undefined;
  return parseLandingBillingCatalog(window.__PALEN_LANDING_BILLING_CATALOG__) || undefined;
}
